import React from "react"
import { useNavigate } from "react-router-dom"
import { Campaign } from "@/types/djangio"
import {
    LandingPageContainer,
    LandingPageHeader,
    ActionButtonsGrid,
    ActionButton,
    ButtonIcon,
    ButtonText,
} from "./LandingPage.styles"
import { VoterIconBackground, CandidatesIconBackground, CalendarIconBackground } from "./LandingPageIconBackgrounds"

interface LandingPageProps {
    campaign: Campaign
    /**
     * Indicates whether there is an upcoming election.
     * When false, we render the "no elections" CTA layout (registration + officials only).
     */
    hasUpcomingElection?: boolean
}

export const LandingPage: React.FC<LandingPageProps> = ({ campaign, hasUpcomingElection = true }) => {
    const navigate = useNavigate()

    const getButtonLabel = (configKey: string, defaultLabel: string): string => {
        return campaign.configs?.[configKey] || defaultLabel
    }

    const handleButtonClick = (path: string) => {
        navigate(`/campaign/${campaign.id}${path}`)
    }

    const registrationDefaultLabel = hasUpcomingElection ? "Check Voter Registration" : "Confirm Registration"
    const electedOfficialsDefaultLabel = hasUpcomingElection ? "Check Elected officials" : "Review Elected Officials"

    return (
        <LandingPageContainer>
            <LandingPageHeader>Make sure you're prepared by:</LandingPageHeader>

            <ActionButtonsGrid>
                <ActionButton
                    className="voter-btn"
                    onClick={() => handleButtonClick("/voter-information")}
                    aria-label={`View ${getButtonLabel("voter_information", "voter registration")}`}
                    data-cy="gotv-voter-info-button"
                >
                    <ButtonIcon className="voter-icon">
                        <i className="fa fa-user" aria-hidden="true" />
                        <span className="user-check" aria-hidden="true">
                            <i className="fa fa-check" aria-hidden="true" />
                        </span>
                        <VoterIconBackground />
                    </ButtonIcon>
                    <ButtonText>{getButtonLabel("voter_information", registrationDefaultLabel)}</ButtonText>
                </ActionButton>

                {hasUpcomingElection && (
                    <>
                        <ActionButton
                            className="candidates-btn"
                            onClick={() => handleButtonClick("/candidates")}
                            aria-label={`View ${getButtonLabel("candidates", "candidates")}`}
                            data-cy="gotv-candidates-button"
                        >
                            <ButtonIcon className="candidates-icon">
                                <i className="fa fa-users" aria-hidden="true" />
                                <CandidatesIconBackground />
                            </ButtonIcon>
                            <ButtonText>{getButtonLabel("candidates", "View Candidates")}</ButtonText>
                        </ActionButton>

                        <ActionButton
                            className="calendar-btn"
                            onClick={() => handleButtonClick("/key-dates")}
                            aria-label={`View ${getButtonLabel("calendar", "calendar")}`}
                            data-cy="gotv-calendar-button"
                        >
                            <ButtonIcon className="calendar-icon">
                                <i className="fa fa-calendar" aria-hidden="true" />
                                <CalendarIconBackground />
                            </ButtonIcon>
                            <ButtonText>{getButtonLabel("calendar", "Look up Calendar")}</ButtonText>
                        </ActionButton>
                    </>
                )}

                <ActionButton
                    className="officials-btn"
                    onClick={() => handleButtonClick("/elected-officials")}
                    aria-label={`View ${getButtonLabel("elected_officials", "elected officials")}`}
                    data-cy="gotv-elected-officials-button"
                >
                    <ButtonIcon className="officials-icon">
                        <i className="fa fa-gavel" aria-hidden="true" />
                    </ButtonIcon>
                    <ButtonText>{getButtonLabel("elected_officials", electedOfficialsDefaultLabel)}</ButtonText>
                </ActionButton>
            </ActionButtonsGrid>
        </LandingPageContainer>
    )
}
